import React, { useState } from "react";
import { Button } from "../../../design/button";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "../../../design/dialog";
import { Fieldset, Field, Label, ErrorMessage } from "../../../design/fieldset";
import { Textarea } from "../../../design/textarea";
import { useReport } from "../../../contexts/ReportContext";
import { useAccount } from "../../../contexts/AccountContext";
import { Visit, ReportStatus } from "@visitproof/types";

interface Props {
  visit: Visit;
}

const CreateReportButton: React.FC<Props> = ({ visit }) => {
  const { createReport } = useReport();
  const { account } = useAccount();
  const [isOpen, setIsOpen] = useState(false);
  const [report, setReport] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!visit.supervisorId || !visit.id) {
      setError("No supervisor assigned to this visit");
      return;
    }

    if (!account?.id) {
      setError("No account selected. Please try again.");
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const newReport = await createReport({
        status: "pending",
        report: report.trim(),
        visitId: visit.id,
        caseId: visit.caseId,
        supervisorId: visit.supervisorId,
        accountId: account.id,
      });

      // Close dialog and redirect to the new report
      setIsOpen(false);
      window.location.href = `/report/${newReport.id}`;
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError("An unknown error occurred while creating the report");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      setIsOpen(false);
      setReport("");
      setError(null);
    }
  };

  return (
    <>
      <Button type="button" onClick={() => setIsOpen(true)}>
        Create Report
      </Button>
      
      <Dialog open={isOpen} onClose={handleClose}>
        <DialogTitle>Create Visit Report</DialogTitle>
        <DialogBody>
          {error && <ErrorMessage className="mb-4">{error}</ErrorMessage>}
          
          <Fieldset>
            <Field>
              <Label htmlFor="report">Report Content</Label>
              <Textarea
                id="report"
                value={report}
                placeholder="Enter the visit report details here..."
                onChange={(e) => setReport(e.target.value)}
                disabled={isSaving}
                rows={15}
                className="w-full"
              />
            </Field>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSaving || !report.trim()}
          >
            {isSaving ? "Creating..." : "Create Report"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateReportButton; 